import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Modal } from 'react-bootstrap';
import { IoDownloadOutline, IoCloudUploadOutline, IoDocumentTextOutline, IoImageOutline } from 'react-icons/io5';
import { ReactComponent as RemoveIcon } from '../../../assets/remove.svg';
import {
    fetchPackagingUnitDocuments,
    uploadPackagingUnitDocuments,
    deletePackagingUnitDocument,
    downloadPackagingUnitDocument,
} from '../../../store/actions/packagingUnitDocument';
import { confirmPackagingUnitSupplierDocs } from '../../../store/actions/packagingUnit';
import { IoCheckmarkCircle, IoAlertCircleOutline } from 'react-icons/io5';

const PAGE_SIZE = 10;

const getPageRange = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
    if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '…', current - 1, current, current + 1, '…', total];
};

const MAX_SIZE_BYTES = 500 * 1024 * 1024;
const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.webp';
const NO_VALUE = '__none__';
const ALL_VALUE = '__all__';

const filterLabelStyle = {
    fontSize: 10.5, fontWeight: 700, color: '#5B6178',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
};

const filterSelectStyle = { width: 200, fontSize: 13, borderColor: '#D7DAEA' };

const CHECKLIST_GROUPS = [
    {
        group: 'A) Podklady účinné k augustu 2026 (čl. 5, 11)',
        items: [
            { id: 'HEAVY_METALS', label: 'Ťažké kovy (čl. 5 ods. 4)' },
            { id: 'PFAS', label: 'PFAS (čl. 5 ods. 5)' },
            { id: 'REUSABLE', label: 'Opakovane použiteľný obal (čl. 11)' },
        ],
    },
    {
        group: 'B) Všeobecné technické podklady (Príloha VII)',
        items: [
            { id: 'MATERIAL_COMPOSITION', label: 'Materiálové zloženie' },
            { id: 'TECHNICAL_DRAWINGS', label: 'Výrobné výkresy / technický list obalu' },
            { id: 'STANDARDS_LIST', label: 'Zoznam uplatnených noriem' },
            { id: 'TEST_PROTOCOLS', label: 'Protokoly o skúškach' },
            { id: 'CONTACT_SENSITIVE', label: 'Dokumentácia pre obal citlivý na kontakt (čl. 16 ods. 2)' },
        ],
    },
    {
        group: 'C) Budúce požiadavky – odporúčané zbierať vopred (čl. 6, 10; termín 2030)',
        items: [
            { id: 'RECYCLABILITY', label: 'Recyklovateľnosť (čl. 6)' },
            { id: 'MINIMIZATION', label: 'Minimalizácia obalu (čl. 10)' },
        ],
    },
    {
        group: 'Ostatné',
        items: [
            { id: 'OTHER', label: 'Iné' },
        ],
    },
];

const CHECKLIST_MAP = Object.fromEntries(
    CHECKLIST_GROUPS.flatMap(g => g.items.map(i => [i.id, i.label]))
);

const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (value) =>
    new Date(value).toLocaleString('sk-SK', { dateStyle: 'medium', timeStyle: 'short' });

const FileTypeIcon = ({ mimeType }) =>
    mimeType === 'application/pdf'
        ? <IoDocumentTextOutline size={17} style={{ color: '#A5432B', flexShrink: 0 }} />
        : <IoImageOutline size={17} style={{ color: '#3B6EC8', flexShrink: 0 }} />;

const Tag = ({ label }) => (
    <span style={{
        fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 999,
        backgroundColor: '#EEF0F8', color: '#5B6178', whiteSpace: 'nowrap',
    }}>
        {label}
    </span>
);

const PackagingUnitDocuments = ({ unit }) => {
    const dispatch = useDispatch();
    const documents = useSelector(state => state.packagingUnitDocument.data);
    const loading = useSelector(state => state.packagingUnitDocument.loading);
    const uploading = useSelector(state => state.packagingUnitDocument.uploading);

    const fileInputRef = useRef(null);
    const [docToDelete, setDocToDelete] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [pendingFiles, setPendingFiles] = useState(null);
    const [selectedComponentId, setSelectedComponentId] = useState(NO_VALUE);
    const [selectedChecklistId, setSelectedChecklistId] = useState(NO_VALUE);
    const [confirming, setConfirming] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [page, setPage] = useState(1);
    const [componentFilter, setComponentFilter] = useState(ALL_VALUE);
    const [checklistFilter, setChecklistFilter] = useState(ALL_VALUE);

    const isConfirmed = !!unit?.supplierDocsConfirmedAt;

    const components = unit?.components || [];

    useEffect(() => {
        if (unit?._id) {
            dispatch(fetchPackagingUnitDocuments(unit._id));
            setPage(1);
        }
    }, [dispatch, unit?._id]);

    useEffect(() => {
        setPage(1);
    }, [componentFilter, checklistFilter]);

    // Voľby filtra sú odvodené z toho, čo je reálne nahraté (nie len z aktuálnych komponentov jednotky) -
    // aby sa dal vyfiltrovať aj dokument priradený k medzičasom vymazanému komponentu.
    const componentFilterOptions = useMemo(() => {
        const seen = new Map();
        documents.forEach((d) => {
            if (!d.componentId || seen.has(d.componentId)) return;
            const current = components.find(c => c._id === d.componentId);
            seen.set(d.componentId, current ? current.component : d.componentName);
        });
        return Array.from(seen.entries());
    }, [documents, components]);

    const checklistFilterOptions = useMemo(() => {
        const seen = new Map();
        documents.forEach((d) => {
            if (!d.checklistItemId || seen.has(d.checklistItemId)) return;
            seen.set(d.checklistItemId, CHECKLIST_MAP[d.checklistItemId] || d.checklistItemLabel || d.checklistItemId);
        });
        return Array.from(seen.entries());
    }, [documents]);

    const filteredDocuments = documents.filter((d) => {
        const componentMatch = componentFilter === ALL_VALUE
            ? true
            : componentFilter === NO_VALUE ? !d.componentId : d.componentId === componentFilter;
        const checklistMatch = checklistFilter === ALL_VALUE
            ? true
            : checklistFilter === NO_VALUE ? !d.checklistItemId : d.checklistItemId === checklistFilter;
        return componentMatch && checklistMatch;
    });

    const totalBytes = documents.reduce((sum, d) => sum + (d.size || 0), 0);
    const usagePercent = Math.min((totalBytes / MAX_SIZE_BYTES) * 100, 100);
    const canUpload = true;

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        e.target.value = '';
        if (!files.length) return;
        setPendingFiles(files);
        setSelectedComponentId(NO_VALUE);
        setSelectedChecklistId(NO_VALUE);
    };

    const handleConfirmUpload = () => {
        if (!pendingFiles) return;
        const component = components.find(c => c._id === selectedComponentId);
        const checklistLabel = CHECKLIST_MAP[selectedChecklistId];
        dispatch(uploadPackagingUnitDocuments({
            files: pendingFiles,
            packagingUnitId: unit._id,
            componentId: component?._id,
            componentName: component?.component,
            checklistItemId: selectedChecklistId !== NO_VALUE ? selectedChecklistId : undefined,
            checklistItemLabel: checklistLabel,
        }));
        setPendingFiles(null);
    };

    const handleDownload = (doc) => {
        setDownloadingId(doc._id);
        dispatch(downloadPackagingUnitDocument({
            documentId: doc._id,
            packagingUnitId: unit._id,
            originalName: doc.originalName,
        }))
            .unwrap()
            .catch(() => {})
            .finally(() => setDownloadingId(null));
    };

    const handleConfirm = () => {
        setConfirming(true);
        dispatch(confirmPackagingUnitSupplierDocs(unit._id))
            .unwrap()
            .catch(() => {})
            .finally(() => setConfirming(false));
    };

    const handleDelete = () => {
        if (!docToDelete) return;
        dispatch(deletePackagingUnitDocument({ documentId: docToDelete._id, packagingUnitId: unit._id }))
            .unwrap()
            .then(() => setDocToDelete(null))
            .catch(() => {});
    };

    return (
        <div>
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="fw-semibold" style={{ color: '#0B1157', fontSize: 14 }}>Nahrané súbory</div>
                    <span className="text-muted small">PDF, JPEG, PNG, WebP · max 10 MB na súbor</span>
                </div>
                <Button
                    disabled={uploading || !canUpload}
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-custom-small d-inline-flex align-items-center gap-2"
                >
                    <IoCloudUploadOutline size={15} />
                    {uploading ? 'Nahrávanie...' : 'Nahrať súbory'}
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED}
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
            </div>

            {/* Storage usage */}
            <div className="mb-3 p-3" style={{ background: '#F4F5FE', borderRadius: 12 }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#5B6178' }}>Využité úložisko</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#0B1157' }}>
                        {formatBytes(totalBytes)} · {documents.length} súborov
                    </span>
                </div>
                <div style={{ height: 6, borderRadius: 99, background: '#DDDFF0', overflow: 'hidden' }}>
                    <div
                        style={{
                            height: '100%',
                            width: `${usagePercent}%`,
                            borderRadius: 99,
                            background: usagePercent > 80 ? '#C97D2C' : '#0B1157',
                            transition: 'width 0.3s ease',
                        }}
                    />
                </div>
            </div>

            {/* Confirmation banner */}
            <div
                className="d-flex align-items-center gap-3 mb-3"
                style={{
                    backgroundColor: isConfirmed ? '#E6F4EA' : '#FBEBD8',
                    border: `1px solid ${isConfirmed ? '#8FCBA6' : '#C97D2C'}`,
                    borderRadius: 12,
                    padding: '12px 16px',
                }}
            >
                {isConfirmed
                    ? <IoCheckmarkCircle size={22} style={{ color: '#198754', flexShrink: 0 }} />
                    : <IoAlertCircleOutline size={22} style={{ color: '#C97D2C', flexShrink: 0 }} />}
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: isConfirmed ? '#198754' : '#C97D2C' }}>
                        {isConfirmed ? 'Dokumenty sú potvrdené' : 'Dokumenty nie sú potvrdené'}
                    </div>
                    {isConfirmed && unit.supplierDocsConfirmedByName && (
                        <div className="text-muted" style={{ fontSize: 11.5 }}>
                            Potvrdil: {unit.supplierDocsConfirmedByName} · {new Date(unit.supplierDocsConfirmedAt).toLocaleString('sk-SK', { dateStyle: 'medium', timeStyle: 'short' })}
                        </div>
                    )}
                    {!isConfirmed && (
                        <div className="text-muted" style={{ fontSize: 11.5 }}>
                            Po skontrolovaní súborov potvrďte, že dokumenty sú v poriadku. Pri zmene podkladov je potrebné znovu vykonať posúdenie zhody.
                        </div>
                    )}
                </div>
                {!isConfirmed && documents.length > 0 && (
                    <Button
                        disabled={confirming}
                        onClick={handleConfirm}
                        className="btn-custom-small d-inline-flex align-items-center gap-2"
                        style={{ flexShrink: 0 }}
                    >
                        <IoCheckmarkCircle size={15} />
                        {confirming ? 'Ukladám...' : 'Potvrdiť dokumenty'}
                    </Button>
                )}
            </div>

            {/* Filter */}
            {documents.length > 0 && (
                <div
                    className="d-flex flex-wrap align-items-end gap-3 mb-3 p-3"
                    style={{ background: '#F4F5FE', borderRadius: 12 }}
                >
                    <div>
                        <div style={filterLabelStyle}>Komponent</div>
                        <Form.Select
                            size="sm"
                            style={filterSelectStyle}
                            value={componentFilter}
                            onChange={e => setComponentFilter(e.target.value)}
                        >
                            <option value={ALL_VALUE}>Všetky</option>
                            <option value={NO_VALUE}>Bez komponentu</option>
                            {componentFilterOptions.map(([id, label]) => (
                                <option key={id} value={id}>{label}</option>
                            ))}
                        </Form.Select>
                    </div>
                    <div>
                        <div style={filterLabelStyle}>Typ dokumentu</div>
                        <Form.Select
                            size="sm"
                            style={{ ...filterSelectStyle, width: 240 }}
                            value={checklistFilter}
                            onChange={e => setChecklistFilter(e.target.value)}
                        >
                            <option value={ALL_VALUE}>Všetky</option>
                            <option value={NO_VALUE}>Bez typu</option>
                            {checklistFilterOptions.map(([id, label]) => (
                                <option key={id} value={id}>{label}</option>
                            ))}
                        </Form.Select>
                    </div>
                    {(componentFilter !== ALL_VALUE || checklistFilter !== ALL_VALUE) && (
                        <button
                            type="button"
                            onClick={() => { setComponentFilter(ALL_VALUE); setChecklistFilter(ALL_VALUE); }}
                            style={{
                                border: 'none', background: 'transparent', color: '#5B6178',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '6px 2px',
                            }}
                        >
                            Zrušiť filter
                        </button>
                    )}
                </div>
            )}

            {/* File list */}
            {loading && documents.length === 0 ? (
                <div className="text-center text-muted py-3 small">Načítavam...</div>
            ) : documents.length === 0 ? (
                <div
                    className="text-center py-4"
                    style={{ background: '#F4F5FE', borderRadius: 14, color: '#9AA0B4' }}
                >
                    <IoCloudUploadOutline size={30} style={{ marginBottom: 6, opacity: 0.5 }} />
                    <div style={{ fontSize: 13 }}>Zatiaľ žiadne súbory.</div>
                </div>
            ) : filteredDocuments.length === 0 ? (
                <div
                    className="text-center py-4"
                    style={{ background: '#F4F5FE', borderRadius: 14, color: '#9AA0B4' }}
                >
                    <div style={{ fontSize: 13 }}>Žiadne dokumenty nezodpovedajú zvolenému filtru.</div>
                </div>
            ) : (() => {
                const totalPages = Math.ceil(filteredDocuments.length / PAGE_SIZE);
                const safePage = Math.min(page, totalPages || 1);
                const pagedDocs = filteredDocuments.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
                const pageRange = getPageRange(safePage, totalPages);
                return (
                <>
                <div style={{ borderRadius: 14, overflowX: 'auto', border: '1px solid #EEF0F8' }}>
                    <table style={{ width: '100%', minWidth: 580, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#F4F5FE' }}>
                                <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', whiteSpace: 'nowrap' }}>Komponent</th>
                                <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', whiteSpace: 'nowrap' }}>Typ dokumentu</th>
                                <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left' }}>Súbor</th>
                                <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'right', width: 80 }}>Akcie</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedDocs.map((doc, i) => (
                                <tr
                                    key={doc._id}
                                    onMouseEnter={() => setHoveredRow(doc._id)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                    style={{
                                        background: hoveredRow === doc._id ? '#EEF1FB' : ((safePage - 1) * PAGE_SIZE + i) % 2 === 0 ? '#fff' : '#F9FAFF',
                                        borderTop: '1px solid #EEF0F8',
                                    }}
                                >
                                    <td style={{ padding: '10px 12px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                        {doc.componentId ? (() => {
                                            const current = components.find(c => c._id === doc.componentId);
                                            if (current) return <Tag label={current.component} />;
                                            return (
                                                <span
                                                    title="Komponent bol vymazaný"
                                                    style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999, backgroundColor: '#FEF3F2', color: '#9AA0B4', textDecoration: 'line-through', whiteSpace: 'nowrap' }}
                                                >
                                                    {doc.componentName}
                                                </span>
                                            );
                                        })() : <span style={{ color: '#C8CBDA', fontSize: 12 }}>—</span>}
                                    </td>
                                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                                        {doc.checklistItemLabel
                                            ? <Tag label={doc.checklistItemLabel} />
                                            : <span style={{ color: '#C8CBDA', fontSize: 12 }}>—</span>}
                                    </td>
                                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                                        <div className="d-flex align-items-center gap-2">
                                            <FileTypeIcon mimeType={doc.mimeType} />
                                            <div style={{ minWidth: 0 }}>
                                                <div
                                                    style={{
                                                        fontSize: 13, fontWeight: 600, color: '#0B1157',
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                    }}
                                                    title={doc.originalName}
                                                >
                                                    {doc.originalName}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#9AA0B4', marginTop: 2 }}>
                                                    {formatBytes(doc.size)} · {formatDate(doc.createdAt)}
                                                    {doc.uploadedByName ? ` · ${doc.uploadedByName}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'right' }}>
                                        <div className="d-flex gap-1 justify-content-end">
                                            <Button
                                                className="btnIcon"
                                                disabled={downloadingId === doc._id}
                                                onClick={() => handleDownload(doc)}
                                            >
                                                <IoDownloadOutline className="icon" size={18} style={{ color: '#718094' }} />
                                            </Button>
                                            <Button className="btnIcon" onClick={() => setDocToDelete(doc)}>
                                                <RemoveIcon className="icon icon-remove" alt="Vymazať" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                        <span style={{ fontSize: 12, color: '#9AA0B4' }}>
                            {filteredDocuments.length} súborov · strana {safePage} z {totalPages}
                        </span>
                        <div className="d-flex gap-1">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={safePage === 1}
                                style={{ border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 13, fontWeight: 600, cursor: safePage === 1 ? 'default' : 'pointer', backgroundColor: '#F1F2F6', color: safePage === 1 ? '#C8CBDA' : '#5B6178' }}
                            >‹</button>
                            {pageRange.map((p, i) =>
                                p === '…' ? (
                                    <span key={`e-${i}`} style={{ padding: '4px 6px', fontSize: 13, color: '#9AA0B4', alignSelf: 'center' }}>…</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        style={{ border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 32, backgroundColor: safePage === p ? '#0B1157' : '#F1F2F6', color: safePage === p ? '#4DFFFF' : '#5B6178' }}
                                    >{p}</button>
                                )
            )}
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={safePage === totalPages}
                                style={{ border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 13, fontWeight: 600, cursor: safePage === totalPages ? 'default' : 'pointer', backgroundColor: '#F1F2F6', color: safePage === totalPages ? '#C8CBDA' : '#5B6178' }}
                            >›</button>
                        </div>
                    </div>
                )}
                </>
                );
            })()}


            {/* Upload modal */}
            <Modal show={!!pendingFiles} onHide={() => setPendingFiles(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Nahrať súbory</Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex flex-column gap-3">
                    <div className="text-muted small">
                        {pendingFiles?.length === 1 ? '1 súbor pripravený na nahranie.' : `${pendingFiles?.length} súborov pripravených na nahranie.`}
                    </div>

                    {components.length > 0 && (
                        <div>
                            <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>Komponent</Form.Label>
                            <Form.Select
                                size="sm"
                                value={selectedComponentId}
                                onChange={e => setSelectedComponentId(e.target.value)}
                            >
                                <option value={NO_VALUE}>— Bez komponentu —</option>
                                {components.map(c => (
                                    <option key={c._id} value={c._id}>{c.component}</option>
                                ))}
                            </Form.Select>
                        </div>
                    )}

                    <div>
                        <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>Typ dokumentu</Form.Label>
                        <Form.Select
                            size="sm"
                            value={selectedChecklistId}
                            onChange={e => setSelectedChecklistId(e.target.value)}
                        >
                            <option value={NO_VALUE}>— Nevybrané —</option>
                            {CHECKLIST_GROUPS.map(g => (
                                <optgroup key={g.group} label={g.group}>
                                    {g.items.map(item => (
                                        <option key={item.id} value={item.id}>{item.label}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </Form.Select>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setPendingFiles(null)}>Zrušiť</Button>
                    <Button variant="primary" disabled={uploading} onClick={handleConfirmUpload}>
                        {uploading ? 'Nahrávanie...' : 'Nahrať'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete modal */}
            <Modal show={!!docToDelete} onHide={() => setDocToDelete(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Vymazať dokument</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Naozaj chcete vymazať <strong>{docToDelete?.originalName}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDocToDelete(null)}>Zrušiť</Button>
                    <Button variant="danger" onClick={handleDelete}>Vymazať</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PackagingUnitDocuments;
